// src/components/Blog/AdminDeleteBlog.jsx
import React, { useState, useEffect } from "react";
import api from "../../api";
// ===== TipTap (READ-ONLY RENDER) =====
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import MathExtension from "./MathExtension";
import CustomImageExtension from "./CustomImageExtension";

/* =====================================================
   🧠 SAFE READ-ONLY CONTENT RENDERER
   - Handles TipTap JSON
   - Handles legacy HTML
   - ESLint-safe (no conditional hooks)
===================================================== */
const ReadOnlyBlogContent = ({ content }) => {
  let parsed = null;
  let isJson = false;

  // Detect JSON safely (NO hooks here)
  try {
    parsed = JSON.parse(content);
    isJson = parsed?.type === "doc";
  } catch {
    isJson = false;
  }

  // ✅ Hook is ALWAYS called (important)
  const editor = useEditor({
    extensions: [StarterKit, MathExtension, CustomImageExtension],
    editable: false,
    content: isJson ? parsed : "", // empty if HTML
  });

  // Update editor only when valid JSON
  useEffect(() => {
    if (editor && isJson) {
      editor.commands.setContent(parsed);
    }
  }, [editor, isJson, parsed]);

  // CASE 1: TipTap JSON
  if (isJson) {
    if (!editor) return null;

    return (
      <EditorContent
        editor={editor}
        className="tiptap-editor border rounded p-3 mb-3"
      />
    );
  }

  // CASE 2: HTML (old / ReactQuill content)
  return (
    <div
      className="border rounded p-3 mb-3"
      dangerouslySetInnerHTML={{ __html: content }}
    />
  );
};

const AdminDeleteBlog = () => {
  const [blogs, setBlogs] = useState([]);
  const [loadingId, setLoadingId] = useState(null);
  const token = localStorage.getItem("token");

  // FETCH BLOGS
  useEffect(() => {
    const fetchBlogs = async () => {
      try {
        const res = await api.get("/api/blog/getall", {
          headers: { Authorization: `Bearer ${token}` },
        });
        setBlogs(res.data);
      } catch (err) {
        console.error("Error fetching blogs:", err);
      }
    };
    fetchBlogs();
  }, [token]);

  // DELETE BLOG
  const handleDelete = async (id) => {
    if (!window.confirm(`Are you sure you want to delete this blog?`)) return;

    setLoadingId(id);
    try {
      await api.delete(`/api/blog/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      alert("Blog deleted successfully");
      setBlogs((prev) => prev.filter((b) => b.id !== id));
    } catch (err) {
      console.error(err);
      alert("Delete failed");
    } finally {
      setLoadingId(null);
    }
  };

  return (
    <div className="container py-4">
      <h3 className="mb-4">🗑️ Manage Blogs (Delete)</h3>

      {blogs.length === 0 ? (
        <p>No blogs available.</p>
      ) : (
        blogs.map((blog) => (
          <div
            key={blog.id}
            className="border rounded-lg p-4 mb-4 shadow-sm bg-white"
          >
            <h4 className="fw-bold mb-3">{blog.title}</h4>

            {/* ✅ SAFE CONTENT RENDER */}
            <ReadOnlyBlogContent content={blog.textContent} />

            <button
              className="btn btn-danger"
              onClick={() => handleDelete(blog.id)}
              disabled={loadingId === blog.id}
            >
              {loadingId === blog.id ? "Deleting..." : "Delete"}
            </button>
          </div>
        ))
      )}
    </div>
  );
};

export default AdminDeleteBlog;
